import { useState, useEffect, useCallback } from 'react';
import dashboardApi from '../services/dashboardApi';

export const useDashboard = () => {
  const [stats, setStats] = useState(null);
  const [todayStats, setTodayStats] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all dashboard data using new API methods
      const [
        stepsData,
        weeklyStats,
        activityResponse,
        recentResponse,
        challengeData
      ] = await Promise.all([
        dashboardApi.getUserStepsData(),
        dashboardApi.getWeeklyStats(),
        dashboardApi.getActivityData('7d'),
        dashboardApi.getRecentActivity(10),
        dashboardApi.getCurrentChallenge()
      ]);
      
      setTodayStats(stepsData);
      setStats(weeklyStats);
      setActivityData(activityResponse.data || activityResponse || []);
      setRecentActivity(recentResponse.activities || recentResponse || []);
      setCurrentChallenge(challengeData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.message || 'Failed to load dashboard data');
      
      // Set fallback data on error
      setTodayStats({ steps: 0, goal: 10000, distance: 0, calories: 0 });
      setStats({ weeklyAverage: 0, weeklyTotal: 0, totalAchievements: 0 });
      setActivityData([]);
      setRecentActivity([]);
      setCurrentChallenge(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshStats = useCallback(async () => {
    try {
      const [stepsData, weeklyStats] = await Promise.all([
        dashboardApi.getUserStepsData(),
        dashboardApi.getWeeklyStats()
      ]);
      setTodayStats(stepsData);
      setStats(weeklyStats);
    } catch (error) {
      console.error('Error refreshing stats:', error);
    }
  }, []);

  const refreshActivity = useCallback(async () => {
    try {
      const [activityResponse, recentResponse] = await Promise.all([
        dashboardApi.getActivityData('7d'),
        dashboardApi.getRecentActivity(10)
      ]);
      
      setActivityData(activityResponse.data || activityResponse || []);
      setRecentActivity(recentResponse.activities || recentResponse || []);
    } catch (error) {
      console.error('Error refreshing activity:', error);
    }
  }, []);

  const refreshData = useCallback(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    stats,
    todayStats,
    activityData,
    recentActivity,
    currentChallenge,
    loading,
    error,
    refreshData,
    refreshStats,
    refreshActivity,
    setError
  };
};